/**
 * Initial camera framing for the explorer map.
 *
 * The map used to open on a fixed centre + zoom (BRAND.mapCenter / mapZoom,
 * zoom 3.9 — clamped up to the map's minZoom of 4). At 4 the 1,000px-wide
 * split map spans ~86° of longitude, so Australia sat small in the middle
 * with Indonesia, PNG and the Philippines filling the top of the frame.
 *
 * Instead we fit a bounding box to the map's ACTUAL pixel size. The camera is
 * computed here (pure Web-Mercator maths, no google.maps dependency) rather
 * than with map.fitBounds(), because GoogleMapComponent re-applies its
 * `center`/`zoom` props right after the map mounts — a bare fitBounds() in
 * onMapReady was undone a frame later. Computing the view lets the caller
 * write the same centre/zoom into that prop state, so the two agree.
 */
import { getCountry, getCountryBounds } from './brand';

export interface BoundsLiteral {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface MapView {
  center: { lat: number; lng: number };
  zoom: number;
}

/**
 * Mainland Australia + Tasmania. Deliberately tighter than getCountryBounds()
 * (a bounds-SEARCH box that pads out to -45/155) so the continent fills the
 * frame: Cape York (~-10.7) to South East Cape (~-43.6), Steep Point (~113.2)
 * to Cape Byron (~153.6).
 */
export const AU_MAINLAND_TAS_BOUNDS: Readonly<BoundsLiteral> = Object.freeze({
  north: -10,
  south: -44,
  east: 154,
  west: 112,
});

/** The box the explorer opens on when nothing (search, region, trip) is active. */
export function getDefaultFrameBounds(): BoundsLiteral {
  // escapeto can be browsing AU too — same frame either brand.
  if (getCountry() === 'AU') return { ...AU_MAINLAND_TAS_BOUNDS };
  return getCountryBounds();
}

const TILE_SIZE = 256;
const MAX_LAT = 85.05112878;

/** Web-Mercator y (radians) for a latitude in degrees. */
function mercatorY(lat: number): number {
  const clamped = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  return 0.5 * Math.log((1 + s) / (1 - s));
}

function inverseMercatorY(y: number): number {
  return ((2 * Math.atan(Math.exp(y)) - Math.PI / 2) * 180) / Math.PI;
}

/**
 * Centre + zoom that fits `bounds` inside a `width`×`height` pixel viewport
 * with `padding` px kept clear on every side. Fractional by default (vector
 * maps); pass `integerZoom` for a raster Google map.
 *
 * Returns null when the viewport has no size (e.g. a display:none map), so
 * callers can fall back to fitBounds or skip framing.
 */
export function computeFitView(
  bounds: BoundsLiteral,
  size: { width: number; height: number },
  options: {
    padding?: number;
    minZoom?: number;
    maxZoom?: number;
    /**
     * Floor to a whole zoom level. Our Google map renders RASTER tiles
     * (requests carry `!1i<z>`), and a raster map snaps a fractional zoom to
     * the nearest level — so a fitted 4.58 became 5 and cropped Perth and the
     * whole east coast. Flooring is the only rounding that always keeps the
     * box in view.
     */
    integerZoom?: boolean;
    /**
     * With integerZoom: round UP once the fractional zoom reaches this
     * (e.g. 0.75), trading a sliver of crop at the edges for a whole level
     * closer. Australia in a 1000px map is 4.8: zoom 4 shows half of Asia,
     * zoom 5 shows Australia. Off by default (never crop).
     */
    roundUpFrom?: number;
  } = {}
): MapView | null {
  const { width, height } = size;
  if (!(width > 0) || !(height > 0)) return null;
  const { north, south, west } = bounds;
  if (![north, south, bounds.east, west].every(Number.isFinite)) return null;

  const padding = Math.max(0, options.padding ?? 24);
  const innerW = Math.max(1, width - 2 * padding);
  const innerH = Math.max(1, height - 2 * padding);

  let lngSpan = bounds.east - west;
  if (lngSpan < 0) lngSpan += 360; // crosses the antimeridian
  const ySpan = Math.abs(mercatorY(north) - mercatorY(south));

  // pixels-per-degree of longitude = TILE * 2^z / 360
  // pixels-per-radian of mercator y = TILE * 2^z / (2π)
  const zoomX = lngSpan > 0 ? Math.log2((innerW * 360) / (TILE_SIZE * lngSpan)) : Infinity;
  const zoomY = ySpan > 0 ? Math.log2((innerH * 2 * Math.PI) / (TILE_SIZE * ySpan)) : Infinity;
  let zoom = Math.min(zoomX, zoomY);
  if (!Number.isFinite(zoom)) zoom = options.maxZoom ?? 15;

  if (typeof options.maxZoom === 'number') zoom = Math.min(zoom, options.maxZoom);
  // Floor (never round) so the box is never cropped: to a whole level for a
  // raster map, else to 2dp.
  if (options.integerZoom) {
    const whole = Math.floor(zoom + 1e-9);
    const up = typeof options.roundUpFrom === 'number' && zoom - whole >= options.roundUpFrom;
    zoom = up ? whole + 1 : whole;
  } else {
    zoom = Math.floor(zoom * 100) / 100;
  }
  if (typeof options.minZoom === 'number') zoom = Math.max(zoom, options.minZoom);

  let lng = west + lngSpan / 2;
  if (lng > 180) lng -= 360;
  const lat = inverseMercatorY((mercatorY(north) + mercatorY(south)) / 2);

  return { center: { lat, lng }, zoom };
}

export interface RectLike {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Size of `rect` after clipping by every rect in `clips` (ancestors that hide
 * overflow). Pure, so it is unit-tested; getVisibleMapSize is the DOM side.
 */
export function clippedSize(rect: RectLike, clips: RectLike[]): { width: number; height: number } {
  let { left, top, right, bottom } = rect;
  for (const c of clips) {
    left = Math.max(left, c.left);
    top = Math.max(top, c.top);
    right = Math.min(right, c.right);
    bottom = Math.min(bottom, c.bottom);
  }
  return { width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/**
 * The part of the map element its layout actually shows: its box clipped by
 * every ancestor that hides overflow. Deliberately NOT clipped by the window:
 * the split map sits under the hero, so at load only a sliver of it is on
 * screen, but it is sized to one viewport (minus the sticky results bar) and
 * is fully visible once scrolled to — framing for the sliver would zoom out
 * to nothing.
 */
export function getVisibleMapSize(el: Element | null | undefined): { width: number; height: number } {
  if (!el || typeof window === 'undefined') return { width: 0, height: 0 };
  const clips: RectLike[] = [];
  let node: Element | null = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const cs = window.getComputedStyle(node);
    if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
      clips.push(node.getBoundingClientRect());
    }
    node = node.parentElement;
  }
  return clippedSize(el.getBoundingClientRect(), clips);
}

/** True when two camera states are the same (within float noise). */
export function isSameView(a: MapView | null | undefined, b: MapView | null | undefined): boolean {
  if (!a || !b) return false;
  return (
    Math.abs(a.center.lat - b.center.lat) < 1e-6 &&
    Math.abs(a.center.lng - b.center.lng) < 1e-6 &&
    Math.abs(a.zoom - b.zoom) < 1e-6
  );
}

/**
 * A view fitted to the part of the map you can see when something covers the
 * bottom `insetPx` of it (the phone planner's bottom sheet): the same zoom,
 * the centre moved south by half the inset in screen pixels (Web Mercator),
 * so the framed area sits in the middle of the uncovered part.
 */
export function shiftCenterForInset(view: MapView, insetPx: number): MapView {
  if (!insetPx) return view;
  const worldPx = 256 * Math.pow(2, view.zoom);
  const latRad = (view.center.lat * Math.PI) / 180;
  const y = (0.5 - Math.log((1 + Math.sin(latRad)) / (1 - Math.sin(latRad))) / (4 * Math.PI)) * worldPx;
  const y2 = Math.min(worldPx, Math.max(0, y + insetPx / 2));
  const n = Math.PI - (2 * Math.PI * y2) / worldPx;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { center: { lat, lng: view.center.lng }, zoom: view.zoom };
}

/**
 * The lat/lng under pixel (x, y) of a `width`×`height` map centred on
 * `center` at `zoom` (Web Mercator, 256px tiles). Lets a fixed on-screen
 * crosshair be read without a projection overlay.
 */
export function pointToLatLng(
  center: { lat: number; lng: number },
  zoom: number,
  width: number,
  height: number,
  x: number,
  y: number
): { lat: number; lng: number } {
  const worldPx = TILE_SIZE * Math.pow(2, zoom);
  const cx = ((center.lng + 180) / 360) * worldPx;
  const cy = (0.5 - mercatorY(center.lat) / (2 * Math.PI)) * worldPx;
  const px = cx + (x - width / 2);
  const py = cy + (y - height / 2);
  let lng = (px / worldPx) * 360 - 180;
  if (lng > 180) lng -= 360;
  if (lng < -180) lng += 360;
  const lat = inverseMercatorY((0.5 - py / worldPx) * 2 * Math.PI);
  return { lat, lng };
}

/**
 * The phone planner's empty-map frame. A 390px-wide raster map can't show
 * Australia whole at a useful zoom (zoom 4 is ~480px wide; zoom 3 is mostly
 * sea), so: zoom 4, Cape York tucked under the chip row (no Indonesia or PNG
 * filling the top), centred a little east so the east coast, where most
 * trips run, is in view. `height` is the full map element; `topPx` is where
 * the north coast should sit.
 */
export function phoneCountryView(
  height: number,
  topPx: number,
  opts: { zoom?: number; north?: number; centerLng?: number } = {}
): MapView {
  const zoom = opts.zoom ?? 4;
  const north = opts.north ?? -10.2;
  const worldPx = TILE_SIZE * Math.pow(2, zoom);
  const yNorth = (0.5 - mercatorY(north) / (2 * Math.PI)) * worldPx;
  // The map centre sits half the element below its top edge.
  const yCenter = yNorth + (height / 2 - topPx);
  const lat = inverseMercatorY((0.5 - yCenter / worldPx) * 2 * Math.PI);
  return { center: { lat, lng: opts.centerLng ?? 136.6 }, zoom };
}

/**
 * A raster map only zooms in whole levels, so a fitted box nearly always
 * leaves slack. Centring it (what fitBounds does) splits that slack above
 * and below: on a phone the empty planner opened on Papua New Guinea above
 * the routes. This fit uses the largest whole zoom that keeps `bounds`
 * inside the padded viewport, then pins the box's TOP edge to the top
 * padding, so the spare room falls below the routes (open sea just above
 * the sheet) instead of over a neighbouring country's labels.
 */
export function fitTopAnchored(
  bounds: BoundsLiteral,
  size: { width: number; height: number },
  pad: { top: number; right: number; bottom: number; left: number },
  options: { minZoom?: number; maxZoom?: number } = {}
): MapView | null {
  const { width, height } = size;
  if (!(width > 0) || !(height > 0)) return null;
  const innerW = Math.max(1, width - pad.left - pad.right);
  const innerH = Math.max(1, height - pad.top - pad.bottom);
  let lngSpan = bounds.east - bounds.west;
  if (lngSpan < 0) lngSpan += 360;
  const yN = mercatorY(bounds.north);
  const yS = mercatorY(bounds.south);
  const ySpan = Math.abs(yN - yS);
  const zoomX = lngSpan > 0 ? Math.log2((innerW * 360) / (TILE_SIZE * lngSpan)) : Infinity;
  const zoomY = ySpan > 0 ? Math.log2((innerH * 2 * Math.PI) / (TILE_SIZE * ySpan)) : Infinity;
  let zoom = Math.floor(Math.min(zoomX, zoomY, options.maxZoom ?? 15) + 1e-9);
  if (typeof options.minZoom === 'number') zoom = Math.max(zoom, options.minZoom);
  const world = TILE_SIZE * Math.pow(2, zoom);
  // Horizontal: centred in the padded box.
  const midLng = bounds.west + lngSpan / 2;
  const lng = midLng + (((pad.right - pad.left) / 2) * 360) / world;
  // Vertical: the box's north edge sits `pad.top` px below the map's top.
  // Mercator y grows north here, so the centre is south of the edge.
  const northPx = (yN * world) / (2 * Math.PI);
  const centerPx = northPx - (height / 2 - pad.top);
  const lat = inverseMercatorY((centerPx * 2 * Math.PI) / world);
  return { center: { lat, lng: lng > 180 ? lng - 360 : lng }, zoom };
}
